import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../environments/environment';
import { AuthService } from '../services/auth/auth.service';
import { AuthFailureNoticeService } from '../services/auth/auth-failure-notice.service';
import { SessionHintService } from '../services/auth/session-hint.service';
import { buildPageLoadFailureNotice } from '../utils/page-load-failure.util';
import { resolveDiagnosticScopeFromUrl } from '../utils/navigation-diagnostic-scope.util';
import { UserAuth } from '../../types/commons';

/**
 * Admits a user to the authenticated area — registered on `/mbrs` as BOTH
 * `canActivate` (entering the area) and `canActivateChild` (every page change
 * inside it), so a session that ended mid-use is caught BEFORE the destination
 * page loads rather than by that page's first API call (P.O., 2026-10-08).
 *
 * It always asks the backend (`AuthService.verifySession()`). The cached
 * `isAuthenticated()` signal decides only what a "no" MEANS:
 *
 * - cached `false` — the app already knows the user is signed out (voluntary
 *   logout then Back, a forced logout that already ran): silent redirect, no
 *   request. Pinned by the "known-unauthenticated" spec.
 * - cached `true` (signed in a moment ago) and the backend says no — the
 *   session ended while in use: `forceLogout('session-expired')`, which stages
 *   the single AUTH toast and wipes customer data exactly like the
 *   interceptor's 401 path.
 * - cached `null` (cold load) and the backend says no — silent redirect, UNLESS
 *   `SessionHintService` says this browser held a session that ended unseen
 *   (its cookie expired while no tab was open): then the single `MBRS-AUTH`
 *   toast is staged once. A first visit or a manual logout stays silent.
 * - a logout ran while the check was in flight (`authTeardownGeneration`
 *   moved) — stop; `forceLogout` already owns the navigation.
 * - the check cannot get an answer — signed in a moment ago: let the page load
 *   (a failed probe is not proof of logout; the page's own request reports its
 *   own NET/SRV code). Cold load: stage the classified code and go to login.
 *
 * Sheet switches inside `/mbrs/filing` are query-param navigations, which do
 * not re-run these guards under the default `runGuardsAndResolvers`.
 */
export const isAuthenticatedGuard: CanActivateFn = async (
  next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Promise<boolean> => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const authFailureNotice = inject(AuthFailureNoticeService);
  const sessionHint = inject(SessionHintService);

  // Auth-domain boundary: dev sessions are not customers. A dev who
  // navigates to /mbrs/* gets bounced to the dev landing instead of
  // being silently let into the customer area. The reverse direction
  // (customer in /dev-{slug}/*) is enforced symmetrically by isDevGuard.
  const redirectDevHome = (): false => {
    const slug = environment.devAreaSlug;
    if (slug)
      router.navigate([`/dev-${slug}`, 'logs', 'api-audit']);
    return false;
  };

  // A cold-load check that could not complete — the backend is unreachable or
  // answered something unusable. No route has mounted, so there is no page to
  // toast on: stage the classified failure for the login page through
  // `AuthFailureNoticeService` (the same mechanism every AUTH-related redirect
  // uses). The staged value is the full composed summary, since this failure
  // is not always AUTH.
  const redirectUnverified = (error: unknown): false => {
    console.error('Auth guard error:', error);
    const notice = buildPageLoadFailureNotice('AUTH_GUARD', error);
    authFailureNotice.stage(notice.summary);
    router.navigate(['/auth/login']);
    return false;
  };

  if (authService.isAuthenticated() === false) {
    router.navigate(['/auth/login']);
    return false;
  }

  // Read BEFORE the request: `verifySession()` rewrites the signal on answer.
  const wasSignedIn = authService.isAuthenticated() === true;
  // A logout (SignalR ForceLogout push, cross-tab) that runs while the check
  // is in flight bumps this. forceLogout then owns the navigation, so a late
  // answer must not fail open, log out again, navigate, or stage a notice.
  const generationBefore = authService.authTeardownGeneration;

  let session: UserAuth;
  try {
    session = await firstValueFrom(authService.verifySession());
  }
  catch (error) {
    if (authService.authTeardownGeneration !== generationBefore)
      return false;
    if (wasSignedIn) {
      if (!environment.production)
        console.warn('[Is Authenticated Guard] Session check failed mid-session; letting the page load.', error);
      return true;
    }
    return redirectUnverified(error);
  }

  if (authService.authTeardownGeneration !== generationBefore)
    return false;

  if (!session.isAuthenticated) {
    if (wasSignedIn) {
      // forceLogout owns the navigation to /auth/login (authReason state and
      // its in-progress latch), so do not start a competing one here.
      // `router.url` is still the page being LEFT while guards run, so the
      // AUTH code names where the user was. Read here, in the same synchronous
      // block as the call, so nothing can move it in between.
      authService.forceLogout('session-expired', {
        diagnosticScope: resolveDiagnosticScopeFromUrl(router.url) ?? undefined,
      });
      return false;
    }
    // Cold load, backend says no. If this browser held a session that ended
    // unseen (the expired cookie is already gone), stage MBRS-AUTH once;
    // otherwise (first visit, manual logout) stay silent.
    sessionHint.stageNoticeIfLapsed();
    router.navigate(['/auth/login']);
    return false;
  }

  if (session.isDev === true)
    return redirectDevHome();

  // A live customer session: keep the hint current, which also covers sessions
  // that began before the hint existed.
  sessionHint.markSessionExists();

  if (wasSignedIn)
    return true;

  // Cold load: wait for the CSRF token before allowing route activation, so
  // the page's first mutation cannot race the token.
  try {
    await firstValueFrom(authService.getCsrfToken());
    return true;
  }
  catch (error) {
    return redirectUnverified(error);
  }
};
